import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { ArrowRight, AlertCircle } from 'lucide-react'
import { Badge, Button, Input, Modal } from '@/components/ui'

export interface HandoffProspect {
  id: string
  companyName: string
  contactFirstName: string | null
  contactLastName: string | null
  contactRole: string | null
  contactEmail: string | null
  contactPhone: string | null
  city: string | null
  icpScore: number
}

interface HandoffDialogProps {
  prospect: HandoffProspect
  /** Target phase selected via drag: 'lead' or 'proposal'. */
  initialPhase: 'Lead neu' | 'Angebot benötigt'
  onClose: () => void
  onSuccess: (result: { tanssTicketId: string | null }) => void
}

/**
 * Dialog for handing off a prospect to the sales board. On submit it
 * automatically creates a TANSS ticket, sets the sales tag, links the
 * prospect, and saves value/probability in the local deals entry.
 */
export function HandoffDialog({ prospect, initialPhase, onClose, onSuccess }: HandoffDialogProps) {
  const queryClient = useQueryClient()
  const [title, setTitle] = useState(`${prospect.companyName} – Neukunden-Akquise`)
  const [value, setValue] = useState('')
  const [probability, setProbability] = useState(initialPhase === 'Angebot benötigt' ? '50' : '10')
  const [phaseTag, setPhaseTag] = useState<'Lead neu' | 'Angebot benötigt'>(initialPhase)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const contactName = [prospect.contactFirstName, prospect.contactLastName].filter(Boolean).join(' ')

  const handoff = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiClient
        .post(`/acquisition/prospects/${prospect.id}/handoff`, payload)
        .then((r) => r.data),
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      queryClient.invalidateQueries({ queryKey: ['vertrieb', 'prospects'] })
      queryClient.invalidateQueries({ queryKey: ['acquisition'] })
      onSuccess({ tanssTicketId: result?.convertedDealId ?? null })
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.error ??
        err?.response?.data?.message ??
        err?.message ??
        'Handoff fehlgeschlagen. Bitte TANSS-Verbindung prüfen.'
      setErrorMessage(String(msg))
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage(null)
    handoff.mutate({
      createTanssTicket: true,
      title: title.trim(),
      value: Number(value) || 0,
      probability: Number(probability) || (phaseTag === 'Angebot benötigt' ? 50 : 10),
      initialPhaseTag: phaseTag,
    })
  }

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      size="md"
      className="max-w-lg"
      title={
        <span className="inline-flex items-center gap-2">
          An Vertrieb übergeben
          <ArrowRight className="w-4 h-4 text-brand-500" />
        </span>
      }
      description="Legt ein TANSS-Ticket an und verlinkt den Prospect."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            type="submit"
            form="handoff-form"
            loading={handoff.isPending}
          >
            {handoff.isPending ? 'Erstelle TANSS-Ticket...' : 'Übergeben & Ticket anlegen'}
          </Button>
        </>
      }
    >
      <form id="handoff-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-gray-50 rounded-md p-3 text-small">
          <p className="font-medium text-gray-900">{prospect.companyName}</p>
          <div className="text-caption text-gray-600 mt-1 flex flex-wrap gap-x-3 gap-y-1">
            {contactName && (
              <span>
                {contactName}
                {prospect.contactRole ? ` · ${prospect.contactRole}` : ''}
              </span>
            )}
            {prospect.contactEmail && <span>{prospect.contactEmail}</span>}
            {prospect.contactPhone && <span>{prospect.contactPhone}</span>}
            {prospect.city && <span>{prospect.city}</span>}
            <Badge variant="brand" size="sm">ICP {prospect.icpScore}</Badge>
          </div>
        </div>

        <Input
          label="Ticket-Titel"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Deal-Wert (EUR)"
            type="number"
            min={0}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="z. B. 25000"
          />
          <Input
            label="Wahrscheinlichkeit (%)"
            type="number"
            min={0}
            max={100}
            value={probability}
            onChange={(e) => setProbability(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-small font-medium text-gray-700">Einstiegsphase</span>
          <div className="flex gap-2">
            {(['Lead neu', 'Angebot benötigt'] as const).map((opt) => (
              <Button
                key={opt}
                type="button"
                fullWidth
                variant={phaseTag === opt ? 'primary' : 'secondary'}
                onClick={() => {
                  setPhaseTag(opt)
                  if (opt === 'Angebot benötigt' && Number(probability) < 50) setProbability('50')
                  if (opt === 'Lead neu' && Number(probability) > 25) setProbability('10')
                }}
              >
                {opt}
              </Button>
            ))}
          </div>
          <span className="text-caption text-gray-500">
            TANSS-Tag, der beim Anlegen des Tickets gesetzt wird.
          </span>
        </div>

        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 text-small text-red-700 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{errorMessage}</span>
          </div>
        )}
      </form>
    </Modal>
  )
}
